import "@/App.css";
import "@/dark.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";

// Route-level code splitting — each page loads as its own small chunk on
// demand instead of one giant bundle, so first paint is fast.
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const DashboardShell = lazy(() => import("@/pages/DashboardShell"));
const CaseIntakePage = lazy(() => import("@/pages/CaseIntakePage"));
const CaseloadPage = lazy(() => import("@/pages/CaseloadPage"));
const InzInboxPage = lazy(() => import("@/pages/InzInboxPage"));
const MailboxPage = lazy(() => import("@/pages/MailboxPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const FeePresetsPage = lazy(() => import("@/pages/FeePresetsPage"));
const AgentsPage = lazy(() => import("@/pages/AgentsPage"));
const ContactsPage = lazy(() => import("@/pages/ContactsPage"));
const CaseDetailPage = lazy(() => import("@/pages/CaseDetailPage"));
const ClientDossierPage = lazy(() => import("@/pages/ClientDossierPage"));
const AccountantLedgerPage = lazy(() => import("@/pages/AccountantLedgerPage"));
const BackupsPage = lazy(() => import("@/pages/BackupsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const SharedFilePage = lazy(() => import("@/pages/SharedFilePage"));
const ArchivesPage = lazy(() => import("@/pages/ArchivesPage"));
const CleanupReportPage = lazy(() => import("@/pages/CleanupReportPage"));
const PublicSignPage = lazy(() => import("@/pages/PublicSignPage"));
const PublicUploadPage = lazy(() => import("@/pages/PublicUploadPage"));
const PublicPayProofPage = lazy(() => import("@/pages/PublicPayProofPage"));
const BookingAcceptPage = lazy(() => import("@/pages/BookingAcceptPage"));
const SecureMessagePage = lazy(() => import("@/pages/SecureMessagePage"));
const ClientPortalApp = lazy(() => import("@/pages/portal/ClientPortalApp"));
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));
const ClientLoginPage = lazy(() => import("@/pages/ClientLoginPage"));
const PublicEligibilityPage = lazy(() => import("@/pages/PublicEligibilityPage"));
const PublicReviewDonePage = lazy(() => import("@/pages/PublicReviewDonePage"));
const OverviewPage = lazy(() => import("@/pages/OverviewPage"));
const AdviserProfilePage = lazy(() => import("@/pages/AdviserProfilePage"));
const SopPlaybooksPage = lazy(() => import("@/pages/SopPlaybooksPage"));
const IppBrowserPage = lazy(() => import("@/pages/IppBrowserPage"));
const PublicANZSCOSignoffPage = lazy(() => import("@/pages/PublicANZSCOSignoffPage"));
const PublicReviewDeclarePage = lazy(() => import("@/pages/PublicReviewDeclarePage"));
const TeamPhonesPage = lazy(() => import("@/pages/TeamPhonesPage"));
const LeadsPage = lazy(() => import("@/pages/LeadsPage"));
const ReviewsPublicPage = lazy(() => import("@/pages/ReviewsPublicPage"));
const ReviewsAdminPage = lazy(() => import("@/pages/ReviewsAdminPage"));
const PublicLeadIntakePage = lazy(() => import("@/pages/PublicLeadIntakePage"));
const BookingsPage = lazy(() => import("@/pages/BookingsPage"));
const TasksPage = lazy(() => import("@/pages/TasksPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F7F6F3]">
    <div className="w-8 h-8 border-[3px] border-slate-200 border-t-slate-700 rounded-full animate-spin" data-testid="page-loader" />
  </div>
);

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/sign/:token" element={<PublicSignPage />} />
          <Route path="/portal/*" element={<ClientPortalApp />} />
          <Route path="/upload/:token" element={<PublicUploadPage />} />
          <Route path="/pay-proof/:token" element={<PublicPayProofPage />} />
          <Route path="/booking-accept/:token" element={<BookingAcceptPage />} />
          <Route path="/secure/:token" element={<SecureMessagePage />} />
          <Route path="/client/:token" element={<ClientPortalPage />} />
          <Route path="/client-login" element={<ClientLoginPage />} />
          <Route path="/eligibility/:token" element={<PublicEligibilityPage />} />
          <Route path="/anzsco-signoff/:token" element={<PublicANZSCOSignoffPage />} />
          <Route path="/review-declare/:token" element={<PublicReviewDeclarePage />} />
          <Route path="/review-done/:token" element={<PublicReviewDonePage />} />
          <Route path="/reviews" element={<ReviewsPublicPage />} />
          <Route path="/intake-form/:type" element={<PublicLeadIntakePage />} />
          <Route path="/intake-form" element={<Navigate to="/intake-form/individual" replace />} />

          <Route path="/app" element={<Protected><DashboardShell /></Protected>}>
            <Route index element={<OverviewPage />} />
            <Route path="intake" element={<CaseIntakePage />} />
            <Route path="cases" element={<CaseloadPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="inz-inbox" element={<InzInboxPage />} />
            <Route path="mailbox" element={<MailboxPage />} />
            <Route path="cases/:caseId" element={<CaseDetailPage />} />
            <Route path="clients/:clientId/dossier" element={<ClientDossierPage />} />
            <Route path="accountant" element={<Protected roles={["admin", "accountant"]}><AccountantLedgerPage /></Protected>} />
            <Route path="backups" element={<Protected roles={["admin", "sub_admin", "accountant"]}><BackupsPage /></Protected>} />
            <Route path="templates" element={<Protected roles={["admin", "sub_admin", "adviser", "agent"]}><TemplatesPage /></Protected>} />
            <Route path="presets" element={<Protected roles={["admin", "sub_admin"]}><FeePresetsPage /></Protected>} />
            <Route path="agents" element={<Protected roles={["admin", "sub_admin"]}><AgentsPage /></Protected>} />
            <Route path="contacts" element={<Protected roles={["admin", "sub_admin", "adviser", "agent"]}><ContactsPage /></Protected>} />
            <Route path="archives" element={<Protected roles={["admin", "sub_admin"]}><ArchivesPage /></Protected>} />
            <Route path="cleanup" element={<Protected roles={["admin", "sub_admin"]}><CleanupReportPage /></Protected>} />
            <Route path="reviews-admin" element={<Protected roles={["admin", "sub_admin"]}><ReviewsAdminPage /></Protected>} />
            <Route path="settings" element={<Protected roles={["admin", "sub_admin"]}><SettingsPage /></Protected>} />
            <Route path="profile" element={<Protected roles={["admin", "sub_admin", "adviser"]}><AdviserProfilePage /></Protected>} />
            <Route path="knowledge" element={<SopPlaybooksPage />} />
            <Route path="ipp" element={<Protected roles={["admin", "sub_admin", "adviser"]}><IppBrowserPage /></Protected>} />
            <Route path="team/phones" element={<Protected roles={["admin", "sub_admin"]}><TeamPhonesPage /></Protected>} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="analytics" element={<Protected roles={["admin", "sub_admin"]}><AnalyticsPage /></Protected>} />
          </Route>

          <Route path="/s/:token" element={<SharedFilePage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
